/**
 * EzERP Barcode Parsing Library
 * Supports: GS1-128, GS1 DataMatrix, NF-e QR, CT-e, Internal barcodes
 * 
 * GS1 Application Identifiers (AI) Reference:
 * - (01) GTIN - Global Trade Item Number (14 digits)
 * - (10) Batch/Lot Number (up to 20 chars)
 * - (17) Expiration Date (YYMMDD)
 * - (21) Serial Number (up to 20 chars)
 * - (11) Production Date (YYMMDD)
 * - (13) Packaging Date (YYMMDD)
 * - (15) Best Before Date (YYMMDD)
 * - (30) Variable Count (up to 8 digits)
 * - (37) Count of Trade Items (up to 8 digits)
 * - (310x) Net Weight in kg (6 digits, x = decimal places)
 * - (320x) Net Weight in lb (6 digits)
 * - (00) SSCC - Serial Shipping Container Code (18 digits)
 * - (02) GTIN of contained items
 * - (240) Additional Product ID
 * - (241) Customer Part Number
 * - (400) Customer's Purchase Order Number
 * - (410) Ship To Location Code
 * - (420) Ship To Postal Code
 */

// GS1 Application Identifier definitions
export const GS1_APPLICATION_IDENTIFIERS = {
  '00': { name: 'SSCC', length: 18, type: 'fixed', description: 'Serial Shipping Container Code' },
  '01': { name: 'GTIN', length: 14, type: 'fixed', description: 'Global Trade Item Number' },
  '02': { name: 'CONTENT_GTIN', length: 14, type: 'fixed', description: 'GTIN of Contained Items' },
  '10': { name: 'BATCH_LOT', length: 20, type: 'variable', description: 'Batch/Lot Number' },
  '11': { name: 'PROD_DATE', length: 6, type: 'fixed', description: 'Production Date (YYMMDD)' },
  '13': { name: 'PACK_DATE', length: 6, type: 'fixed', description: 'Packaging Date (YYMMDD)' },
  '15': { name: 'BEST_BEFORE', length: 6, type: 'fixed', description: 'Best Before Date (YYMMDD)' },
  '17': { name: 'EXPIRY_DATE', length: 6, type: 'fixed', description: 'Expiration Date (YYMMDD)' },
  '21': { name: 'SERIAL', length: 20, type: 'variable', description: 'Serial Number' },
  '30': { name: 'VAR_COUNT', length: 8, type: 'variable', description: 'Variable Count' },
  '37': { name: 'COUNT', length: 8, type: 'variable', description: 'Count of Trade Items' },
  '240': { name: 'ADDITIONAL_ID', length: 30, type: 'variable', description: 'Additional Product ID' },
  '241': { name: 'CUST_PART_NO', length: 30, type: 'variable', description: 'Customer Part Number' },
  '310': { name: 'NET_WEIGHT_KG', length: 6, type: 'fixed', description: 'Net Weight (kg)' },
  '320': { name: 'NET_WEIGHT_LB', length: 6, type: 'fixed', description: 'Net Weight (lb)' },
  '400': { name: 'ORDER_NUMBER', length: 30, type: 'variable', description: 'Customer Purchase Order' },
  '410': { name: 'SHIP_TO_LOC', length: 13, type: 'fixed', description: 'Ship To Location Code' },
  '420': { name: 'SHIP_TO_POSTAL', length: 20, type: 'variable', description: 'Ship To Postal Code' },
  '8020': { name: 'REF_PAYMENT', length: 25, type: 'variable', description: 'Payment Reference' },
};

// Group Separator character (ASCII 29) - used in GS1-128
const GS = String.fromCharCode(29);
// Function 1 Symbol Character (often represented as ]C1 or ]d2)
const FNC1_PATTERNS = [']C1', ']d2', ']e0', ']J1'];

/**
 * Barcode type detection
 */
export const BarcodeType = {
  GS1_128: 'GS1_128',
  GS1_DATAMATRIX: 'GS1_DATAMATRIX',
  NFE_ACCESS_KEY: 'NFE_ACCESS_KEY',
  NFE_QR: 'NFE_QR',
  CTE_ACCESS_KEY: 'CTE_ACCESS_KEY',
  EAN_13: 'EAN_13',
  EAN_8: 'EAN_8',
  UPC_A: 'UPC_A',
  CODE_128: 'CODE_128',
  INTERNAL: 'INTERNAL',
  UNKNOWN: 'UNKNOWN'
};

/**
 * Detect barcode type from raw scan data
 */
export const detectBarcodeType = (rawData) => {
  if (!rawData || typeof rawData !== 'string') {
    return BarcodeType.UNKNOWN;
  }

  const data = rawData.trim();

  // NF-e Access Key (44 digits starting with specific patterns)
  if (/^\d{44}$/.test(data) && /^[1-5][0-9]/.test(data)) {
    // Check if it looks like NF-e (model 55) or CT-e (model 57)
    const model = data.substring(20, 22);
    if (model === '55') return BarcodeType.NFE_ACCESS_KEY;
    if (model === '57') return BarcodeType.CTE_ACCESS_KEY;
    return BarcodeType.NFE_ACCESS_KEY; // Default to NF-e
  }

  // NF-e QR Code (URL format)
  if (data.includes('nfce') || data.includes('nfe') || data.includes('sefaz')) {
    return BarcodeType.NFE_QR;
  }

  // GS1-128 or GS1 DataMatrix (starts with FNC1 or contains AI patterns)
  if (FNC1_PATTERNS.some(p => data.startsWith(p)) || 
      data.includes(GS) ||
      /^\(01\)/.test(data) ||
      /^01\d{14}/.test(data)) {
    // DataMatrix typically has ]d2 prefix
    if (data.startsWith(']d2') || data.startsWith(']e0')) {
      return BarcodeType.GS1_DATAMATRIX;
    }
    return BarcodeType.GS1_128;
  }

  // EAN-13 (13 digits)
  if (/^\d{13}$/.test(data)) {
    return BarcodeType.EAN_13;
  }

  // EAN-8 (8 digits)
  if (/^\d{8}$/.test(data)) {
    return BarcodeType.EAN_8;
  }

  // UPC-A (12 digits)
  if (/^\d{12}$/.test(data)) {
    return BarcodeType.UPC_A;
  }

  // Internal barcode patterns (EzERP specific)
  if (/^(ASN|PO|SO|TO|RTV|PR|LOC|PLT|CTN)-/.test(data)) {
    return BarcodeType.INTERNAL;
  }

  return BarcodeType.UNKNOWN;
};

/**
 * Parse GS1-128 barcode
 * Extracts Application Identifiers and their values
 */
export const parseGS1_128 = (rawData) => {
  const result = {
    type: BarcodeType.GS1_128,
    raw: rawData,
    valid: false,
    fields: {},
    errors: []
  };

  let data = rawData.trim();

  // Remove FNC1 prefix if present
  FNC1_PATTERNS.forEach(p => {
    if (data.startsWith(p)) {
      data = data.substring(p.length);
    }
  });

  // Remove parentheses format (01)12345678901234 → 0112345678901234
  data = data.replace(/\((\d+)\)/g, '$1');

  let position = 0;
  let aiCount = 0;
  const maxAIs = 20; // Safety limit

  while (position < data.length && aiCount < maxAIs) {
    let matched = false;

    // Try to match AI patterns (longest first)
    const aiKeys = Object.keys(GS1_APPLICATION_IDENTIFIERS).sort((a, b) => b.length - a.length);

    for (const ai of aiKeys) {
      if (data.substring(position).startsWith(ai)) {
        const aiDef = GS1_APPLICATION_IDENTIFIERS[ai];
        position += ai.length;

        let value;
        if (aiDef.type === 'fixed') {
          value = data.substring(position, position + aiDef.length);
          position += aiDef.length;
        } else {
          // Variable length - read until GS, next AI, or end
          const remaining = data.substring(position);
          const gsPos = remaining.indexOf(GS);
          
          // Look ahead for next AI (try common AIs)
          let nextAiPos = remaining.length;
          for (const nextAi of Object.keys(GS1_APPLICATION_IDENTIFIERS)) {
            const idx = remaining.indexOf(nextAi);
            // Make sure we find an AI that makes sense (not just any digit match)
            // AI must be at a reasonable position (after at least 1 char of data)
            if (idx > 0 && idx < nextAiPos) {
              // Validate it looks like an AI (followed by appropriate data)
              const afterAi = remaining.substring(idx + nextAi.length);
              if (afterAi.length > 0) {
                nextAiPos = idx;
              }
            }
          }
          
          if (gsPos !== -1 && gsPos < nextAiPos) {
            value = remaining.substring(0, gsPos);
            position += gsPos + 1;
          } else if (nextAiPos < remaining.length) {
            value = remaining.substring(0, nextAiPos);
            position += nextAiPos;
          } else {
            value = remaining;
            position = data.length;
          }
          
          // Truncate if exceeds max length
          if (value.length > aiDef.length) {
            value = value.substring(0, aiDef.length);
          }
        }

        // Parse dates
        if (['EXPIRY_DATE', 'PROD_DATE', 'PACK_DATE', 'BEST_BEFORE'].includes(aiDef.name)) {
          result.fields[aiDef.name] = {
            raw: value,
            parsed: parseGS1Date(value),
            ai: ai,
            description: aiDef.description
          };
        } 
        // Parse weights (decimal handling)
        else if (aiDef.name.startsWith('NET_WEIGHT')) {
          const decimalPlaces = parseInt(ai.charAt(ai.length - 1)) || 0;
          const numValue = parseInt(value) / Math.pow(10, decimalPlaces);
          result.fields[aiDef.name] = {
            raw: value,
            parsed: numValue,
            unit: aiDef.name.includes('KG') ? 'kg' : 'lb',
            ai: ai,
            description: aiDef.description
          };
        }
        else {
          result.fields[aiDef.name] = {
            raw: value,
            parsed: value,
            ai: ai,
            description: aiDef.description
          };
        }

        matched = true;
        aiCount++;
        break;
      }
    }

    if (!matched) {
      // Skip unknown character
      result.errors.push(`Unknown data at position ${position}: ${data.substring(position, position + 10)}...`);
      position++;
    }
  }

  result.valid = aiCount > 0 && result.errors.length === 0;
  return result;
};

/**
 * Parse GS1 DataMatrix (same AI structure as GS1-128)
 */
export const parseGS1DataMatrix = (rawData) => {
  const result = parseGS1_128(rawData);
  result.type = BarcodeType.GS1_DATAMATRIX;
  return result;
};

/**
 * Parse GS1 date format (YYMMDD)
 * Returns ISO date string
 */
export const parseGS1Date = (yymmdd) => {
  if (!yymmdd || yymmdd.length !== 6) return null;

  const yy = parseInt(yymmdd.substring(0, 2));
  const mm = parseInt(yymmdd.substring(2, 4));
  let dd = parseInt(yymmdd.substring(4, 6));

  // Handle day = 00 (means last day of month)
  if (dd === 0) dd = 31;

  // Year handling: 00-50 = 2000-2050, 51-99 = 1951-1999
  const year = yy <= 50 ? 2000 + yy : 1900 + yy;

  try {
    const date = new Date(year, mm - 1, dd);
    return date.toISOString().split('T')[0];
  } catch {
    return null;
  }
};

/**
 * Parse NF-e Access Key (44 digits)
 * Structure: cUF(2) AAMM(4) CNPJ(14) mod(2) serie(3) nNF(9) tpEmis(1) cNF(8) cDV(1)
 */
export const parseNFeAccessKey = (accessKey) => {
  const result = {
    type: BarcodeType.NFE_ACCESS_KEY,
    raw: accessKey,
    valid: false,
    fields: {},
    errors: []
  };

  if (!accessKey || accessKey.length !== 44) {
    result.errors.push('Access key must be 44 digits');
    return result;
  }

  if (!/^\d{44}$/.test(accessKey)) {
    result.errors.push('Access key must contain only digits');
    return result;
  }

  // Parse fields
  result.fields = {
    cUF: {
      raw: accessKey.substring(0, 2),
      parsed: getUFName(accessKey.substring(0, 2)),
      description: 'State Code (UF)'
    },
    AAMM: {
      raw: accessKey.substring(2, 6),
      parsed: `20${accessKey.substring(2, 4)}-${accessKey.substring(4, 6)}`,
      description: 'Issue Year/Month'
    },
    CNPJ: {
      raw: accessKey.substring(6, 20),
      parsed: formatCNPJ(accessKey.substring(6, 20)),
      description: 'Issuer CNPJ'
    },
    mod: {
      raw: accessKey.substring(20, 22),
      parsed: accessKey.substring(20, 22) === '55' ? 'NF-e' : 
              accessKey.substring(20, 22) === '65' ? 'NFC-e' :
              accessKey.substring(20, 22) === '57' ? 'CT-e' : 'Unknown',
      description: 'Document Model'
    },
    serie: {
      raw: accessKey.substring(22, 25),
      parsed: parseInt(accessKey.substring(22, 25)),
      description: 'Series'
    },
    nNF: {
      raw: accessKey.substring(25, 34),
      parsed: parseInt(accessKey.substring(25, 34)),
      description: 'Invoice Number'
    },
    tpEmis: {
      raw: accessKey.substring(34, 35),
      parsed: getEmissionType(accessKey.substring(34, 35)),
      description: 'Emission Type'
    },
    cNF: {
      raw: accessKey.substring(35, 43),
      parsed: accessKey.substring(35, 43),
      description: 'NF-e Code'
    },
    cDV: {
      raw: accessKey.substring(43, 44),
      parsed: parseInt(accessKey.substring(43, 44)),
      description: 'Check Digit'
    }
  };

  // Validate check digit
  const calculatedDV = calculateNFeDV(accessKey.substring(0, 43));
  if (calculatedDV !== parseInt(accessKey.substring(43, 44))) {
    result.errors.push('Invalid check digit');
  }

  result.valid = result.errors.length === 0;
  return result;
};

/**
 * Parse NF-e QR Code URL
 */
export const parseNFeQR = (qrData) => {
  const result = {
    type: BarcodeType.NFE_QR,
    raw: qrData,
    valid: false,
    fields: {},
    errors: []
  };

  try {
    const url = new URL(qrData);
    const params = new URLSearchParams(url.search);

    // Extract parameters
    if (params.has('chNFe') || params.has('p')) {
      const accessKey = params.get('chNFe') || params.get('p');
      const nfeResult = parseNFeAccessKey(accessKey);
      result.fields = { ...nfeResult.fields };
      result.fields.accessKey = {
        raw: accessKey,
        parsed: accessKey,
        description: 'Access Key'
      };
    }

    if (params.has('nVersao')) {
      result.fields.version = {
        raw: params.get('nVersao'),
        parsed: params.get('nVersao'),
        description: 'QR Version'
      };
    }

    if (params.has('tpAmb')) {
      result.fields.environment = {
        raw: params.get('tpAmb'),
        parsed: params.get('tpAmb') === '1' ? 'Production' : 'Homologation',
        description: 'Environment'
      };
    }

    result.valid = Object.keys(result.fields).length > 0;
  } catch (e) {
    result.errors.push('Invalid QR Code URL format');
  }

  return result;
};

/**
 * Parse CT-e Access Key (same structure as NF-e but model 57)
 */
export const parseCTeAccessKey = (accessKey) => {
  const result = parseNFeAccessKey(accessKey);
  result.type = BarcodeType.CTE_ACCESS_KEY;
  return result;
};

/**
 * Parse internal EzERP barcode
 */
export const parseInternalBarcode = (barcode) => {
  const result = {
    type: BarcodeType.INTERNAL,
    raw: barcode,
    valid: false,
    fields: {},
    errors: []
  };

  const patterns = {
    ASN: /^ASN-(\d{4})-(\d{4,6})$/,
    PO: /^PO-(\d{4})-(\d{4,6})$/,
    SO: /^SO-(\d{4})-(\d{4,6})$/,
    TO: /^TO-(\d{4})-(\d{4,6})$/,
    RTV: /^RTV-(\d{4})-(\d{4,6})$/,
    PR: /^PR-(\d{4})-(\d{4,6})$/,
    LOC: /^LOC-([A-Z0-9]+-[A-Z0-9]+-[A-Z0-9]+)$/,
    PLT: /^PLT-(\d{10,18})$/,
    CTN: /^CTN-(\d{10,18})$/,
  };

  for (const [type, pattern] of Object.entries(patterns)) {
    const match = barcode.match(pattern);
    if (match) {
      result.fields.type = {
        raw: type,
        parsed: type,
        description: 'Document Type'
      };
      result.fields.reference = {
        raw: barcode,
        parsed: barcode,
        description: 'Full Reference'
      };

      if (type === 'LOC') {
        result.fields.location = {
          raw: match[1],
          parsed: match[1],
          description: 'Location Code'
        };
      } else if (type === 'PLT' || type === 'CTN') {
        result.fields.id = {
          raw: match[1],
          parsed: match[1],
          description: type === 'PLT' ? 'Pallet ID' : 'Carton ID'
        };
      } else {
        result.fields.year = {
          raw: match[1],
          parsed: parseInt(match[1]),
          description: 'Year'
        };
        result.fields.number = {
          raw: match[2],
          parsed: parseInt(match[2]),
          description: 'Document Number'
        };
      }

      result.valid = true;
      break;
    }
  }

  if (!result.valid) {
    result.errors.push('Unknown internal barcode format');
  }

  return result;
};

/**
 * Main barcode parser - auto-detects type and parses
 */
export const parseBarcode = (rawData) => {
  const type = detectBarcodeType(rawData);

  switch (type) {
    case BarcodeType.GS1_128:
      return parseGS1_128(rawData);
    case BarcodeType.GS1_DATAMATRIX:
      return parseGS1DataMatrix(rawData);
    case BarcodeType.NFE_ACCESS_KEY:
      return parseNFeAccessKey(rawData);
    case BarcodeType.NFE_QR:
      return parseNFeQR(rawData);
    case BarcodeType.CTE_ACCESS_KEY:
      return parseCTeAccessKey(rawData);
    case BarcodeType.INTERNAL:
      return parseInternalBarcode(rawData);
    case BarcodeType.EAN_13:
    case BarcodeType.EAN_8:
    case BarcodeType.UPC_A:
      return {
        type,
        raw: rawData,
        valid: true,
        fields: {
          GTIN: {
            raw: rawData,
            parsed: rawData.padStart(14, '0'),
            description: 'Product Code'
          }
        },
        errors: []
      };
    default:
      return {
        type: BarcodeType.UNKNOWN,
        raw: rawData,
        valid: false,
        fields: {},
        errors: ['Unable to detect barcode type']
      };
  }
};

/**
 * Validate barcode against expected values
 */
export const validateBarcode = (parsed, expected) => {
  const validation = {
    valid: true,
    matches: {},
    mismatches: []
  };

  if (!parsed.valid) {
    validation.valid = false;
    validation.mismatches.push('Barcode parsing failed');
    return validation;
  }

  for (const [key, expectedValue] of Object.entries(expected)) {
    const field = parsed.fields[key];
    if (!field) {
      validation.mismatches.push(`Missing field: ${key}`);
      validation.valid = false;
    } else if (field.parsed !== expectedValue && field.raw !== expectedValue) {
      validation.mismatches.push(`${key}: expected "${expectedValue}", got "${field.parsed}"`);
      validation.valid = false;
    } else {
      validation.matches[key] = true;
    }
  }

  return validation;
};

// Helper functions
const getUFName = (code) => {
  const UF_CODES = {
    '11': 'RO', '12': 'AC', '13': 'AM', '14': 'RR', '15': 'PA',
    '16': 'AP', '17': 'TO', '21': 'MA', '22': 'PI', '23': 'CE',
    '24': 'RN', '25': 'PB', '26': 'PE', '27': 'AL', '28': 'SE',
    '29': 'BA', '31': 'MG', '32': 'ES', '33': 'RJ', '35': 'SP',
    '41': 'PR', '42': 'SC', '43': 'RS', '50': 'MS', '51': 'MT',
    '52': 'GO', '53': 'DF'
  };
  return UF_CODES[code] || code;
};

const formatCNPJ = (cnpj) => {
  if (cnpj.length !== 14) return cnpj;
  return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`;
};

const getEmissionType = (code) => {
  const types = {
    '1': 'Normal',
    '2': 'Contingência FS-IA',
    '3': 'Contingência SCAN',
    '4': 'Contingência EPEC',
    '5': 'Contingência FS-DA',
    '6': 'Contingência SVC-AN',
    '7': 'Contingência SVC-RS',
    '9': 'Contingência Off-line NFC-e'
  };
  return types[code] || code;
};

const calculateNFeDV = (base43) => {
  const weights = [4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 43; i++) {
    sum += parseInt(base43[i]) * weights[i];
  }
  const remainder = sum % 11;
  return remainder < 2 ? 0 : 11 - remainder;
};

// Export sample barcodes for testing
// GS1-128 format: AI(01)=GTIN(14) + AI(10)=Lot(var) + AI(17)=Expiry(6) + AI(21)=Serial(var)
export const SAMPLE_BARCODES = {
  GS1_128_PHARMA: '010789123456789010LOT12345617231231211234567',
  GS1_128_WITH_WEIGHT: '0107891234567890310512500',
  NFE_ACCESS_KEY: '35260612345678901234550010000012341234567890',
  CTE_ACCESS_KEY: '35260612345678901234570010000012341234567898',
  NFE_QR: 'https://www.nfce.fazenda.sp.gov.br/NFCeConsultaPublica/Paginas/ConsultaQRCode.aspx?chNFe=35260612345678901234550010000012341234567890&nVersao=100&tpAmb=1',
  EAN_13: '7891234567890',
  INTERNAL_ASN: 'ASN-2026-0089',
  INTERNAL_LOC: 'LOC-A-01-01',
  INTERNAL_PLT: 'PLT-00012345678901234567'
};
